import React, { memo } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ButtonSpinner } from '@/components/LoadingSpinner';

// Pagination Controls Component - Copied from Index.tsx
const PaginationControls = memo<{
  totalPages: number;
  currentPage: number;
  totalPeriods: number;
  onPageChange: (page: number) => void;
  onNextPage: () => void;
  onPrevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  isLoading?: boolean;
}>(({ totalPages, currentPage, totalPeriods, onPageChange, onNextPage, onPrevPage, hasNextPage, hasPrevPage, isLoading = false }) => {
  // Chỉ ẩn pagination nếu không có data, luôn hiện nếu có ít nhất 1 period
  if (totalPeriods === 0) return null;
  
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5; // Số trang hiển thị tối đa
    
    // Đảm bảo totalPages ít nhất là 1 nếu có data
    const actualTotalPages = Math.max(1, totalPages);
    
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(actualTotalPages, startPage + showPages - 1);
    
    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  return (
    <div className="flex items-center justify-center mt-2">
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevPage}
          disabled={!hasPrevPage || isLoading}
          className="flex-shrink-0 h-8"
        >
          {isLoading ? (
            <ButtonSpinner className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
          <span className="hidden sm:inline ml-1">Trước</span>
        </Button>
        
        <div className="flex items-center space-x-1 overflow-x-auto max-w-xs lg:max-w-none">
          {getPageNumbers().map(page => (
            <Button
              key={page}
              variant={page === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              className="min-w-[2rem] lg:min-w-[2.5rem] h-8 flex-shrink-0 text-xs lg:text-sm"
            >
              {isLoading && page === currentPage ? (
                <ButtonSpinner className="h-3 w-3" />
              ) : (
                page
              )}
            </Button>
          ))}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={onNextPage}
          disabled={!hasNextPage || isLoading}
          className="flex-shrink-0 h-8"
        >
          <span className="hidden sm:inline mr-1">Sau</span>
          {isLoading ? (
            <ButtonSpinner className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
});

PaginationControls.displayName = 'PaginationControls';

interface ReportFooterProps {
  activeTab: string;
  paginatedPeriods: {
    currentPage: number;
    totalPages: number;
    totalPeriods: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  paginatedGroupPeriods: {
    currentPage: number;
    totalPages: number;
    totalPeriods: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  onNextPage: () => void;
  onPrevPage: () => void;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

/**
 * Clean Fixed Footer Component with Pure Pagination Controls
 * 
 * Features:
 * - Always visible at bottom of screen, no scroll needed
 * - Minimal design with only pagination buttons (Previous, 1, 2, 3, Next)
 * - No duplicate info text - clean UX focused on navigation
 * 
 * CRITICAL: This component MUST be rendered outside all containers
 * for true position: fixed behavior
 */
export const ReportFooter: React.FC<ReportFooterProps> = ({
  activeTab,
  paginatedPeriods,
  paginatedGroupPeriods,
  onNextPage,
  onPrevPage,
  onPageChange,
  isLoading = false,
}) => {
  const currentData = activeTab === 'all' ? paginatedPeriods : paginatedGroupPeriods;

  return (
    <footer className="flex-shrink-0 bg-white border-t shadow-[0_-2px_6px_rgba(0,0,0,0.06)] py-3 px-4">
      <div className="max-w-7xl mx-auto flex justify-center">
        <PaginationControls
          currentPage={currentData.currentPage}
          totalPages={currentData.totalPages}
          totalPeriods={currentData.totalPeriods || 0}
          hasNextPage={currentData.hasNextPage}
          hasPrevPage={currentData.hasPrevPage}
          onNextPage={onNextPage}
          onPrevPage={onPrevPage}
          onPageChange={onPageChange}
          isLoading={isLoading}
        />
      </div>
    </footer>
  );
};

export default ReportFooter;